import { createSelector } from '@reduxjs/toolkit';

import { MyFavoritesItemStatusTab } from '@/pages/MyItems/MyFavorites/types/MyFavoritesPage.types';
import {
    getLimitedSavedItemIdsBySaveTime,
    getSavedItems,
    getSavedItemsPagination,
} from '@/redux/modules/item/saved/savedItems.selectors';
import { getLimitedSortedRecentItemIdsFromState } from '@/redux/modules/recentItem';
import { GlobalState } from '@/redux/store';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';

import {
    FetchRecommendedSimilarItemsRequest,
    RECOMMENDED_SIMILAR_ITEMS_CACHE_TIME,
    RECOMMENDED_SIMILAR_ITEMS_SAVED_LOOKBACK_TIME,
    RECOMMENDED_SIMILAR_ITEMS_SEED_LIMIT,
} from './recommendedSimilarItems.types';

const stateSelector = (state: GlobalState) => state;
const limitSelector = (_: GlobalState, limit: number) => limit;

const recommendedSimilarItemsSlice = createSelector(stateSelector, (state) => state.recommendedSimilarItems);
const hasLoadedSavedItemsByMostRecentSaveTime = (state: GlobalState) => {
    const pagination = getSavedItemsPagination(state);
    return (
        state.savedItems.loaded > 0 &&
        pagination.page === 1 &&
        pagination.status === MyFavoritesItemStatusTab.Available &&
        pagination.sort === SearchSortAndDirection.SavedMostRecent
    );
};
const recentlyFetchedSavedItemIdsSelector = createSelector(
    [getSavedItems, getSavedItemsPagination],
    (savedItemIds, pagination) =>
        pagination.status === MyFavoritesItemStatusTab.Available &&
        pagination.sort === SearchSortAndDirection.SavedMostRecent
            ? savedItemIds.slice(0, RECOMMENDED_SIMILAR_ITEMS_SEED_LIMIT)
            : []
);

const itemIdsSelector = createSelector(recommendedSimilarItemsSlice, (state) => state.itemIds);
const loadedSelector = createSelector(recommendedSimilarItemsSlice, (state) => state.loaded);
const loadingSelector = createSelector(recommendedSimilarItemsSlice, (state) => state.loading);
const storedSeedItemIdsSelector = createSelector(recommendedSimilarItemsSlice, (state) => state.seedItemIds);

export const getRecommendedSimilarItemIdsFromState = createSelector(
    [itemIdsSelector, limitSelector],
    (itemIds, limit) => (limit ? itemIds.slice(0, limit) : itemIds)
);

export const getRecommendedSimilarItemsSavedSeedItemIds = (state: GlobalState) => {
    const savedItemIdsBySaveTime = getLimitedSavedItemIdsBySaveTime(
        state,
        RECOMMENDED_SIMILAR_ITEMS_SEED_LIMIT,
        Date.now() - RECOMMENDED_SIMILAR_ITEMS_SAVED_LOOKBACK_TIME
    );

    if (savedItemIdsBySaveTime.length >= RECOMMENDED_SIMILAR_ITEMS_SEED_LIMIT) {
        return savedItemIdsBySaveTime;
    }

    const recentlyFetchedSavedItemIds = recentlyFetchedSavedItemIdsSelector(state);
    return recentlyFetchedSavedItemIds.length ? recentlyFetchedSavedItemIds : savedItemIdsBySaveTime;
};

export const getRecommendedSimilarItemsViewedSeedItemIds = (state: GlobalState) =>
    getLimitedSortedRecentItemIdsFromState(state, RECOMMENDED_SIMILAR_ITEMS_SEED_LIMIT);

export const getRecommendedSimilarItemsSeedItemIds = (
    state: GlobalState,
    { shouldUseLastTwoSavedLots, shouldUseLastTwoViewedLots }: FetchRecommendedSimilarItemsRequest
) => {
    if (shouldUseLastTwoViewedLots) {
        return getRecommendedSimilarItemsViewedSeedItemIds(state);
    }

    if (shouldUseLastTwoSavedLots) {
        const savedSeedItemIds = getRecommendedSimilarItemsSavedSeedItemIds(state);
        return savedSeedItemIds.length >= RECOMMENDED_SIMILAR_ITEMS_SEED_LIMIT
            ? savedSeedItemIds
            : getRecommendedSimilarItemsViewedSeedItemIds(state);
    }

    return [];
};

export const shouldFetchRecommendedSimilarItems = (
    state: GlobalState,
    request: FetchRecommendedSimilarItemsRequest
) => {
    const isEnabled = request.shouldUseLastTwoViewedLots || request.shouldUseLastTwoSavedLots;

    if (!isEnabled || loadingSelector(state)) {
        return false;
    }

    if (request.shouldUseLastTwoSavedLots && !hasLoadedSavedItemsByMostRecentSaveTime(state)) {
        return true;
    }

    const seedItemIds = getRecommendedSimilarItemsSeedItemIds(state, request);

    const storedSeedItemIds = storedSeedItemIdsSelector(state);
    const hasSeedItemIdsChanged = seedItemIds.join(',') !== storedSeedItemIds.join(',');

    return hasSeedItemIdsChanged || Date.now() - loadedSelector(state) >= RECOMMENDED_SIMILAR_ITEMS_CACHE_TIME;
};
